import { useState } from "react";
import {
  Calendar,
  MapPin,
  Users,
  AlarmClock,
  Copy,
  Pencil,
  Send,
  Video,
  XCircle,
  Share2,
  Check,
  RefreshCwOff,
  Trash2,
} from "lucide-react";
import type { Event } from "../types";
import { EventStatusBadge } from "./EventStatusBadge";
import { useLocale } from "../../../context/LanguageContext";
import { toBcp47, type Locale } from "../../../i18n";

interface EventCardProps {
  event: Event;
  onEdit: (event: Event) => void;
  onDuplicate: (event: Event) => void;
  onPublish: (event: Event) => void;
  onCancel: (event: Event) => void;
  onDelete: (event: Event) => void;
  onStopRecurrence: (event: Event) => void;
}

function formatDate(dateStr: string, locale: Locale): string {
  return new Date(dateStr).toLocaleDateString(toBcp47(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getCreatorName(event: Event): string | null {
  if (!event.creator) return null;
  const { firstName, lastName } = event.creator;
  return [firstName, lastName].filter(Boolean).join(" ") || null;
}

const DEEP_LINK_BASE = "https://kommiters.com/join";

function ShareButton({
  entityType,
  entityId,
}: {
  entityType: "challenge" | "event";
  entityId: string;
}) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);
  const url = `${DEEP_LINK_BASE}/${entityType}/${entityId}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? t("shared.share.copied") : t("shared.share.copyLink")}
      className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" />
      ) : (
        <Share2 className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

export function EventCard({
  event,
  onEdit,
  onDuplicate,
  onPublish,
  onCancel,
  onDelete,
  onStopRecurrence,
}: EventCardProps) {
  const { t, locale } = useLocale();
  const isDraft = event.status === "DRAFT";
  const showDelete = event.status === "DRAFT";
  const showCancel =
    event.status === "UPCOMING" || event.status === "ACTIVE";
  const showEdit =
    event.status === "DRAFT" ||
    event.status === "UPCOMING" ||
    (event.status === "ACTIVE" && !event.isStarted);
  const isVirtual = event.location?.isVirtual;
  const showStopRecurrence =
    event.recurrence?.enabled &&
    event.status !== "COMPLETED" &&
    event.status !== "CANCELLED";
  const creatorName = getCreatorName(event);

  return (
    <div className="group rounded-lg border border-border bg-card p-4 transition-shadow hover:shadow-md">
      {/* Header: title + status badge (top-right) */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-foreground">
            {event.name}
          </h3>
          {creatorName && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("events.card.by", { name: creatorName })}
            </p>
          )}
        </div>
        <EventStatusBadge status={event.status} recurrence={event.recurrence} />
      </div>

      {/* Description */}
      {event.description && (
        <p className="mb-3 line-clamp-2 text-xs text-muted-foreground">
          {event.description}
        </p>
      )}

      {/* Cover image */}
      {event.photoUrl && (
        <div className="mb-3 overflow-hidden rounded-md">
          <img
            src={event.photoUrl}
            alt={event.name}
            className="h-32 w-full object-cover"
          />
        </div>
      )}

      {/* Details */}
      <div className="mb-3 space-y-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          <span>
            {formatDate(event.startDate, locale)} – {formatDate(event.endDate, locale)}
          </span>
          {event.startTime && (
            <span className="text-foreground/60">
              {event.startTime}
              {event.endTime ? ` – ${event.endTime}` : ""}
            </span>
          )}
        </div>

        {/* Location */}
        {isVirtual ? (
          <div className="flex items-center gap-1.5">
            <Video className="h-3.5 w-3.5" />
            <span>{t("events.card.virtualEvent")}</span>
            {event.location?.meetingLink && (
              <span className="truncate text-primary">
                {t("events.card.linkConfigured")}
              </span>
            )}
          </div>
        ) : event.location?.city ? (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            <span className="truncate">
              {[event.location.address, event.location.city, event.location.country]
                .filter(Boolean)
                .join(", ")}
            </span>
          </div>
        ) : null}

        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          <span>{t("events.card.participants", { count: event.participantCount })}</span>
        </div>

        {event.daysUntilStart > 0 && (
          <div className="flex items-center gap-1.5">
            <AlarmClock className="h-3.5 w-3.5" />
            <span>{t("events.card.startsIn", { count: event.daysUntilStart })}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 border-t border-border pt-3">
        {isDraft && (
          <button
            type="button"
            onClick={() => onPublish(event)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Send className="h-3 w-3" />
            {t("events.card.publish")}
          </button>
        )}
        {showEdit && (
          <button
            type="button"
            onClick={() => onEdit(event)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <Pencil className="h-3 w-3" />
            {t("events.card.edit")}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDuplicate(event)}
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          <Copy className="h-3 w-3" />
          {t("events.card.duplicate")}
        </button>
        {showCancel && (
          <button
            type="button"
            onClick={() => onCancel(event)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-destructive/30 px-2.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <XCircle className="h-3 w-3" />
            {t("events.card.cancel")}
          </button>
        )}
        {showDelete && (
          <button
            type="button"
            onClick={() => onDelete(event)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-destructive/30 px-2.5 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3" />
            {t("events.card.delete")}
          </button>
        )}
        {showStopRecurrence && (
          <button
            type="button"
            onClick={() => onStopRecurrence(event)}
            title={t("events.card.stopRecurring")}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-warning/30 px-2.5 text-xs font-medium text-warning hover:bg-warning/10"
          >
            <RefreshCwOff className="h-3 w-3" />
            {t("events.card.stopRecurring")}
          </button>
        )}
        <ShareButton entityType="event" entityId={event._id} />
      </div>
    </div>
  );
}
